import type { AccountUserSettingDto } from '../../types/api';

export type SraManagedSectionKey = 'Clients' | 'Stats' | 'Account';

export interface SraAccessMenuItem {
  label: string;
  submenu: string;
  settingName: string;
}

export interface SraAccessColumn {
  section: SraManagedSectionKey;
  menuSettingName: string;
  items: SraAccessMenuItem[];
}

const CLIENTS_ALWAYS_VISIBLE_SUBTABS = ['Add Client'] as const;

export const SRA_ACCESS_COLUMNS: SraAccessColumn[] = [
  {
    section: 'Clients',
    menuSettingName: 'enable_clientsmenu',
    items: [
      { label: 'Manage Clients', submenu: 'Manage Clients', settingName: 'enable_viewprofiles' },
      { label: 'Recent Reports', submenu: 'Recent Reports', settingName: 'enable_viewreports' },
      { label: 'Open Admins', submenu: 'Open Admins', settingName: 'enable_viewopenadmins' },
      { label: 'Manage Flags', submenu: 'Manage Flags', settingName: 'enable_viewflags' },
      { label: 'Manage Messages', submenu: 'Manage Messages', settingName: 'enable_viewmessaging' },
    ],
  },
  {
    section: 'Stats',
    menuSettingName: 'enable_statsmenu',
    items: [
      { label: 'Usage', submenu: 'Usage', settingName: 'enable_usagestats' },
      { label: 'Aggregate', submenu: 'Aggregate', settingName: 'enable_aggregate' },
      { label: 'Scheduled', submenu: 'Scheduled', settingName: 'enable_scheduledstats' },
      { label: 'System', submenu: 'System', settingName: 'enable_systemstats' },
      { label: 'Data Export', submenu: 'Data Export', settingName: 'enable_dataexport' },
    ],
  },
  {
    section: 'Account',
    menuSettingName: 'enable_accountmenu',
    items: [
      { label: 'Subscription', submenu: 'Subscription', settingName: 'enable_managesubscription' },
      { label: 'Account Settings', submenu: 'Account Settings', settingName: 'enable_accountsettings' },
      { label: 'Assessment Options', submenu: 'Assessment Options', settingName: 'enable_assessmentoptions' },
      { label: 'Touchpoints', submenu: 'Touchpoints', settingName: 'enable_managetouchpoints' },
      { label: 'Manage Team', submenu: 'Manage Team', settingName: 'enable_manageteams' },
    ],
  },
];

// Account Settings > Settings enables ('-1' = hidden) that, when hidden, also hide the dashboard
// menu settings they govern from the Manage Team access table.
const HIDDEN_ENABLE_TO_SRA_SETTINGS: Record<string, string[]> = {
  enable_patrefmgmt: ['enable_viewflags'],
  enable_messagedirect: ['enable_viewmessaging'],
  enable_schedulemgmt: ['enable_managetouchpoints'],
};

// Account Settings > Advanced settings enables that must be '1' for the menu setting to be offered.
const REQUIRED_ENABLE_TO_SRA_SETTINGS: Record<string, string[]> = {
  enable_openadmins: ['enable_viewopenadmins'],
  enable_scheduledstats: ['enable_scheduledstats'],
  enable_systemstats: ['enable_systemstats'],
  enable_dataexport: ['enable_dataexport'],
};

export const resolveHiddenSraSettings = (
  accountEnables: AccountUserSettingDto[] | undefined
): Set<string> => {
  const hidden = new Set<string>();
  const enabledAccountSettings = new Set<string>();
  (accountEnables ?? []).forEach((entry) => {
    if ((entry.Value ?? '').trim() === '1') {
      enabledAccountSettings.add(normalizeSraSettingName(entry.Name || ''));
    }
  });
  Object.entries(REQUIRED_ENABLE_TO_SRA_SETTINGS).forEach(([enableName, settings]) => {
    if (!enabledAccountSettings.has(enableName)) {
      settings.forEach((name) => hidden.add(name));
    }
  });
  (accountEnables ?? []).forEach((entry) => {
    if ((entry.Value ?? '').trim() !== '-1') {
      return;
    }
    (HIDDEN_ENABLE_TO_SRA_SETTINGS[normalizeSraSettingName(entry.Name || '')] ?? [])
      .forEach((name) => hidden.add(name));
  });
  return hidden;
};

/**
 * HARDCODED RULE: the only per-submenu settings Pro accounts (SRA AccessType 10) can have — two
 * tabs each under Clients (Manage Clients, Recent Reports), Stats (Usage, Aggregate) and Account
 * (Subscription, Account Settings). Manage Team (SraRowEditor) offers only these, and
 * resolveSraMenuVisibility limits every sub-menu to them regardless of what is stored. Assessment
 * Options isn't one of them: Pro's assessment settings live in Account Settings.
 */
export const ACCESS_TYPE_10_ALLOWED_SETTINGS: readonly string[] = [
  'enable_viewprofiles',
  'enable_viewreports',
  'enable_usagestats',
  'enable_aggregate',
  'enable_managesubscription',
  'enable_accountsettings',
];

const SRA_MANAGED_SETTING_NAMES = new Set<string>([
  ...SRA_ACCESS_COLUMNS.map((column) => column.menuSettingName),
  ...SRA_ACCESS_COLUMNS.flatMap((column) => column.items.map((item) => item.settingName)),
].map((name) => name.trim().toLowerCase()));

export interface SraResolvedMenuVisibility {
  mainMenuEnabled: Record<SraManagedSectionKey, boolean>;
  subtabsBySection: Record<SraManagedSectionKey, string[]>;
}

export const normalizeSraSettingName = (value: string): string => value.trim().toLowerCase();

export const isTruthySraSettingValue = (value: string | null | undefined): boolean => {
  if (!value) {
    return false;
  }

  const normalized = value.trim().toLowerCase();
  return normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'enabled' || normalized === 'active';
};

export const sraSettingsToEnableMap = (
  settings: AccountUserSettingDto[] | undefined
): Record<string, boolean> => {
  const map: Record<string, boolean> = {};
  (settings ?? []).forEach((entry) => {
    map[normalizeSraSettingName(entry.Name || '')] = isTruthySraSettingValue(entry.Value);
  });
  return map;
};

export const getSraEnableWithDefault = (
  map: Record<string, boolean>,
  settingName: string,
  defaultValue = true
): boolean => {
  const key = normalizeSraSettingName(settingName);
  if (Object.prototype.hasOwnProperty.call(map, key)) {
    return !!map[key];
  }
  return defaultValue;
};

const resolveColumnBySettingName = (settingName: string): SraAccessColumn | null => {
  const normalized = normalizeSraSettingName(settingName);

  const byMenu = SRA_ACCESS_COLUMNS.find(
    (column) => normalizeSraSettingName(column.menuSettingName) === normalized
  );
  if (byMenu) {
    return byMenu;
  }

  return SRA_ACCESS_COLUMNS.find((column) => column.items.some(
    (item) => normalizeSraSettingName(item.settingName) === normalized
  )) ?? null;
};

export const applySraToggleToEnableMap = (
  current: Record<string, boolean>,
  settingName: string,
  nextValue: boolean
): Record<string, boolean> => {
  const normalizedSetting = normalizeSraSettingName(settingName);
  const next: Record<string, boolean> = {
    ...current,
    [normalizedSetting]: nextValue,
  };

  const column = resolveColumnBySettingName(normalizedSetting);
  if (!column) {
    return next;
  }

  const parentKey = normalizeSraSettingName(column.menuSettingName);
  const itemKeys = column.items.map((item) => normalizeSraSettingName(item.settingName));
  const isParentToggle = parentKey === normalizedSetting;

  if (isParentToggle) {
    next[parentKey] = nextValue;
    itemKeys.forEach((key) => {
      next[key] = nextValue;
    });
    return next;
  }

  if (nextValue) {
    next[parentKey] = true;
    return next;
  }

  const hasAnyEnabledSubmenu = itemKeys.some((key) => getSraEnableWithDefault(next, key, true));
  if (!hasAnyEnabledSubmenu) {
    next[parentKey] = false;
  }

  return next;
};

export const applySraEnableMapToConfigInfo = (
  existing: AccountUserSettingDto[] | undefined,
  enableMap: Record<string, boolean>
): AccountUserSettingDto[] => {
  const existingRows = existing ?? [];
  const seenKeys = new Set<string>();

  const merged = existingRows.map((entry) => {
    const key = normalizeSraSettingName(entry.Name || '');
    seenKeys.add(key);

    if (!SRA_MANAGED_SETTING_NAMES.has(key)) {
      return entry;
    }

    if (!Object.prototype.hasOwnProperty.call(enableMap, key)) {
      return entry;
    }

    return {
      Name: entry.Name,
      Value: enableMap[key] ? '1' : '0',
    };
  });

  Object.keys(enableMap).forEach((key) => {
    if (seenKeys.has(key) || !SRA_MANAGED_SETTING_NAMES.has(key)) {
      return;
    }

    merged.push({
      Name: key,
      Value: enableMap[key] ? '1' : '0',
    });
  });

  return merged;
};

/**
 * Office Online (SRA AccessType 30) users are limited to a chosen set of assessments instead of
 * the Clients/Stats/Account menu table. The selection is stored in AccountSraDto.ConfigInfo as one
 * setting whose Value is a comma-separated list of AssessmentIds, in the order they were selected.
 */
export const SRA_ASSESSMENT_IDS_SETTING = 'assessmentids';

export const parseSraAssessmentIds = (settings: AccountUserSettingDto[] | undefined): string[] => {
  const entry = (settings ?? []).find(
    (setting) => normalizeSraSettingName(setting.Name || '') === SRA_ASSESSMENT_IDS_SETTING
  );
  const seen = new Set<string>();
  const ids: string[] = [];
  (entry?.Value ?? '').split(',').forEach((raw) => {
    const id = raw.trim();
    if (id && !seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
  });
  return ids;
};

/**
 * Returns ConfigInfo with the assessment-id setting set to `assessmentIds`. Leaves the list
 * untouched (same reference) when there is no change — including "empty selection and no existing
 * setting" — so dirty-checking by comparison stays accurate.
 */
export const applySraAssessmentIdsToConfigInfo = (
  existing: AccountUserSettingDto[] | undefined,
  assessmentIds: string[]
): AccountUserSettingDto[] => {
  const rows = existing ?? [];
  const value = assessmentIds.join(',');
  const index = rows.findIndex(
    (setting) => normalizeSraSettingName(setting.Name || '') === SRA_ASSESSMENT_IDS_SETTING
  );

  if (index < 0) {
    return value ? [...rows, { Name: SRA_ASSESSMENT_IDS_SETTING, Value: value }] : rows;
  }
  if ((rows[index].Value ?? '') === value) {
    return rows;
  }
  const next = [...rows];
  next[index] = { ...rows[index], Value: value };
  return next;
};

export const resolveSraMenuVisibility = (
  settings: AccountUserSettingDto[] | undefined,
  accessType?: number,
  // The account's own SRA record AccessType (AccountSraDto.AccessType: 10 = Pro, 11+ = Groups) —
  // the same value AccountSettingsView keys its Pro/Groups modes off. Distinct from `accessType`,
  // the sign-in session's value, which is 10 for anyone entering through the Pro route (Groups
  // accounts included). Undefined until the account data has loaded.
  sraAccessType?: number
): SraResolvedMenuVisibility => {
  const map = sraSettingsToEnableMap(settings);

  const mainMenuEnabled: Record<SraManagedSectionKey, boolean> = {
    Clients: getSraEnableWithDefault(map, 'enable_clientsmenu', true),
    Stats: getSraEnableWithDefault(map, 'enable_statsmenu', true),
    Account: getSraEnableWithDefault(map, 'enable_accountmenu', true),
  };

  // HARDCODED RULE: Hide Clients tab for SRA users (accessType 20-29) for PHI isolation
  if (accessType !== undefined && accessType >= 20 && accessType < 30) {
    mainMenuEnabled.Clients = false;
  }

  const subtabsBySection: Record<SraManagedSectionKey, string[]> = {
    Clients: [],
    Stats: [],
    Account: [],
  };

  SRA_ACCESS_COLUMNS.forEach((column) => {
    const sectionEnabled = mainMenuEnabled[column.section];

    if (!sectionEnabled) {
      subtabsBySection[column.section] = [];
      return;
    }

    const allowed = column.items
      .filter((item) => getSraEnableWithDefault(map, item.settingName, true))
      .map((item) => item.submenu);

    if (column.section === 'Clients') {
      subtabsBySection.Clients = [...CLIENTS_ALWAYS_VISIBLE_SUBTABS, ...allowed];
      return;
    }

    subtabsBySection[column.section] = allowed;
  });

  // HARDCODED RULE: Pro accounts (SRA AccessType 10) only get the two sub-menus per section in
  // ACCESS_TYPE_10_ALLOWED_SETTINGS (Clients: Manage Clients + Recent Reports, Stats: Usage +
  // Aggregate, Account: Subscription + Account Settings) — their assessment settings live in
  // Account Settings, so no Assessment Options either. Groups (11+) keep everything. Only applied
  // once the account's SRA AccessType is known, so a Groups account never loses tabs while data
  // is still loading. Clients' always-visible 'Add Client' stays.
  if (sraAccessType === 10) {
    const proSubmenus = new Set<string>(
      SRA_ACCESS_COLUMNS.flatMap((column) => column.items)
        .filter((item) => ACCESS_TYPE_10_ALLOWED_SETTINGS.includes(normalizeSraSettingName(item.settingName)))
        .map((item) => item.submenu)
    );
    const alwaysVisible = new Set<string>(CLIENTS_ALWAYS_VISIBLE_SUBTABS);
    (Object.keys(subtabsBySection) as SraManagedSectionKey[]).forEach((section) => {
      subtabsBySection[section] = subtabsBySection[section].filter(
        (tab) => proSubmenus.has(tab) || (section === 'Clients' && alwaysVisible.has(tab))
      );
    });
  }

  // Conditionally add 'Manage Accounts' based on accessType (not configurable via settings)
  // Only show for SRA users: accessType >= 20 && < 30
  if (accessType !== undefined && accessType >= 20 && accessType < 30) {
    if (mainMenuEnabled.Account && !subtabsBySection.Account.includes('Manage Accounts')) {
      subtabsBySection.Account.push('Manage Accounts');
    }
  }

  return {
    mainMenuEnabled,
    subtabsBySection,
  };
};
